<script setup lang="ts">
import { ShieldCheckIcon } from "@heroicons/vue/solid"
import { FindAnAdvisorProfile } from "@/types/profile"
import ProfileImage from "@/components/shared/ProfileImage.vue"
import { useFindAnAdvisorProfile } from "@/composables/advisor-profile"
import GradientBG from "@/assets/gradient-blueberry.webp"
import { ref } from "vue"

// TODO: (spk) all advisors should have taken a fiduciary oath.
// rather than link to their signed document, maybe we should link to a marketing
// page that explains why this is a great feature.

const props = defineProps<{ profile: FindAnAdvisorProfile }>()

const { firmLogoURL, profileImageURL } = useFindAnAdvisorProfile(props.profile)

const imgLoaded = ref(false)
</script>

<template>
  <header
    class="bg-gradient-to-r from-blue-700 to-blue-500 px-5 pt-16 pb-14 text-white md:px-12 lg:py-20 relative"
  >
    <div
      class="bg-scroll bg-cover bg-left-top absolute inset-0 z-0"
      :style="`background-image: url(${GradientBG});`"
    />
    <div
      class="container max-w-5xl flex flex-col gap-8 items-center md:flex-row md:justify-center md:gap-12 relative z-10"
    >
      <div>
        <div class="relative">
          <div class="shrink-0 w-60">
            <ProfileImage :src="profileImageURL" />
          </div>

          <div
            class="absolute right-0 bottom-0 translate-x-1/3 translate-y-1/3 transition-opacity"
            :class="imgLoaded ? 'visible opacity-100' : 'opacity-0 invisible'"
          >
            <div class="shrink-0 w-28 rounded-md overflow-hidden shadow-lg">
              <div class="bg-white p-1">
                <img
                  class="object-scale-down w-full h-full"
                  :src="firmLogoURL"
                  :alt="`${profile.firmName} logo`"
                  :onload="(e) => (imgLoaded = true)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <p class="text-blue-200 text-sm font-bold mb-1 lg:text-base">
          <span class="inline-flex items-center gap-x-1">
            <ShieldCheckIcon class="w-5 h-5" />
            Fiduciary Oath
          </span>
        </p>
        <h1
          class="text-3xl leading-8 font-extrabold font-display text-white mb-0.5 lg:text-5xl"
        >
          {{ profile.name }}
        </h1>
        <h2
          class="text-lg leading-5 font-medium text-neutral-100 lg:text-xl lg:leading-6"
        >
          {{ profile.firmName }}
        </h2>

        <div v-if="profile.designations" class="mt-3.5">
          <ul class="flex flex-wrap -mx-0.5">
            <li
              v-for="designation in profile.designations"
              :key="designation"
              class="mx-0.5 mb-0.5"
            >
              <span class="xy-badge-blue">{{ designation }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </header>
</template>
